import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { notificationAPI } from '../../service/api';
import { useSocket } from '../../contexts/SocketContext';
import { FiBell, FiMessageSquare, FiCheckCircle, FiAlertCircle, FiTrash2 } from 'react-icons/fi';
import useAutoRefresh from '../../hooks/useAutoRefresh';

const typeIcons = {
  message: FiMessageSquare,
  demarche: FiCheckCircle,
  community: FiAlertCircle,
  info: FiBell,
};

const typeColors = {
  message: '#3B82F6',
  demarche: '#10B981',
  community: '#C59B6C',
  info: '#6B7280',
};

export default function NotificationsPage() {
  const { socket } = useSocket();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationAPI.getAll();
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread);
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);
  useAutoRefresh(() => fetchNotifications(), 30000);

  useEffect(() => {
    if (!socket) return;
    const handleNew = () => { fetchNotifications(); };
    socket.on('new_message', handleNew);
    socket.on('new_comment', handleNew);
    socket.on('status_update', handleNew);
    return () => {
      socket.off('new_message', handleNew);
      socket.off('new_comment', handleNew);
      socket.off('status_update', handleNew);
    };
  }, [socket, fetchNotifications]);

  const handleMarkAsRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, estLu: true } : n));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {}
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, estLu: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleDelete = async (id) => {
    try {
      await notificationAPI.delete(id);
      const deleted = notifications.find((n) => n.id === id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (deleted && !deleted.estLu) setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {}
  };

  if (loading) {
    return (
      <div className="page-container py-20 text-center">
        <p className="text-navy/60">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="page-container py-10">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold text-navy">Notifications</h1>
          <p className="text-navy/60 mt-1">
            {unreadCount > 0 ? `${unreadCount} non lue${unreadCount > 1 ? 's' : ''}` : 'Tout est à jour'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button onClick={handleMarkAllAsRead} className="text-sm font-medium text-navy/60 hover:text-navy transition-colors underline">
            Tout marquer comme lu
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="text-center py-20">
          <FiBell className="w-12 h-12 mx-auto text-navy/20 mb-4" />
          <p className="text-navy/40">Aucune notification</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((n) => {
            const Icon = typeIcons[n.type] || FiBell;
            const color = typeColors[n.type] || '#6B7280';
            return (
              <div key={n.id}
                className={`flex items-start gap-4 p-4 transition-colors ${n.estLu ? 'bg-white/50' : 'bg-brass/5'}`}
                style={{ borderLeft: `3px solid ${n.estLu ? 'transparent' : color}` }}>
                <div className="mt-0.5" style={{ color }}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={n.lien || '#'} onClick={() => !n.estLu && handleMarkAsRead(n.id)}
                    className="block">
                    <p className={`text-sm ${n.estLu ? 'text-navy/60' : 'text-navy font-medium'}`}>
                      {n.titre}
                    </p>
                    <p className="text-xs text-navy/40 mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-navy/30 mt-1">
                      {new Date(n.dateCreation).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </Link>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!n.estLu && (
                    <button onClick={() => handleMarkAsRead(n.id)}
                      className="p-1 text-navy/30 hover:text-green-600 transition-colors">
                      <FiCheckCircle className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => handleDelete(n.id)}
                    className="p-1 text-navy/30 hover:text-red-500 transition-colors">
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
